import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { MessageSquare, Sparkles, AlertTriangle, Copy, Check } from "lucide-react";
import MadeByBadge from "@/components/brand/MadeByBadge";

/**
 * Detect embedded webviews (in-app browsers) — Google OAuth blocks these
 * with a 403 disallowed_useragent since 2021, so we must send users to the
 * real system browser (Safari/Chrome) BEFORE they try to sign in.
 * Covers Instagram, Facebook, TikTok, LinkedIn, Twitter, WhatsApp, Line,
 * WeChat, and generic Android/iOS WebView signatures.
 */
function isEmbeddedWebView() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const rules = [
    /FBAN|FBAV|FBIOS|FB_IAB/i,           // Facebook / Messenger
    /Instagram/i,                          // Instagram
    /TikTok|BytedanceWebview|musical_ly/i, // TikTok
    /LinkedInApp/i,                        // LinkedIn
    /Twitter/i,                            // Twitter/X
    /Line\//i,                             // Line
    /MicroMessenger/i,                     // WeChat
    /WhatsApp/i,                           // WhatsApp
    /Snapchat/i,                           // Snapchat
    /YaBrowser/i,                          // Yandex
    /; wv\)/i,                             // Generic Android WebView
    /Version\/[\d.]+\s+Mobile\/\w+\s+Safari\/[\d.]+/i && !/CriOS|FxiOS/i,
  ];
  return rules.some((r) => (r instanceof RegExp ? r.test(ua) : r));
}

const GOOGLE_ICON = (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.9 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.9 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.3-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.4 0-9.9-3.4-11.5-8.2l-6.5 5C9.4 39.6 16.1 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.2 5.2C41.4 35.5 44 30.2 44 24c0-1.2-.1-2.4-.4-3.5z" />
  </svg>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const inWebView = useMemo(() => isEmbeddedWebView(), []);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
      navigate("/app", { replace: true });
    } catch (err) {
      // Special case: server returned "email_not_verified" — route them
      // straight to the OTP page instead of showing an error.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        navigate(`/verify-email?email=${encodeURIComponent(detail.email || email)}`, { replace: true });
        return;
      }
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // Google OAuth blocks embedded webviews (403 disallowed_useragent). Steer
    // the user to their system browser BEFORE we redirect to Google.
    if (inWebView) return; // button will be visibly disabled with a banner
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full flex items-stretch bg-[#D4B3FF]" data-testid="login-page">
      {/* Left decorative panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-10 border-r-[3px] border-[#0A0A0A] relative overflow-hidden">
        <div className="flex items-center gap-3">
          <img
            src="/logo.svg"
            alt="Yappo logo"
            className="w-14 h-14 drop-shadow-[6px_6px_0_#0A0A0A]"
            data-testid="login-brand-logo"
          />
          <span className="font-display text-2xl">yappo.</span>
        </div>

        <div>
          <p className="font-display text-6xl xl:text-7xl leading-[0.95] text-[#0A0A0A]">
            Sohbet ama <br />
            <span className="bg-[#CCFF00] px-3 border-hard shadow-hard inline-block -rotate-1">
              cesurca
            </span>
            .
          </p>
          <p className="mt-6 max-w-md text-[#0A0A0A]/80 font-medium text-lg">
            Arkadaşlarınla ve ekibinle canlı sohbet. Okundu bilgisi, yazıyor göstergesi ve emojiler —
            hepsi tek bir Neo-Brutal arayüzde.
          </p>
        </div>

        <div className="flex items-center gap-2 text-sm font-semibold">
          <div className="brut-tape h-6 w-24 border-hard-2" />
          <a
            href="https://tellcelltr.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="login-hero-maker-link"
            className="hover:underline"
          >
            TELL CELL tarafından yapıldı
          </a>
        </div>

        {/* Decorative shapes */}
        <div className="absolute -right-16 top-24 w-48 h-48 rounded-full bg-[#FF9E80] border-hard shadow-hard-lg" />
        <div className="absolute right-20 bottom-40 w-16 h-16 bg-[#CCFF00] border-hard shadow-hard rotate-12" />
      </div>

      {/* Right auth card */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border-hard shadow-hard-lg rounded-2xl p-8 slide-up">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <img
              src="/logo.svg"
              alt="Yappo logo"
              className="w-11 h-11 drop-shadow-[4px_4px_0_#0A0A0A]"
              data-testid="login-brand-logo-mobile"
            />
            <span className="font-display text-xl">yappo.</span>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <Sparkles strokeWidth={3} className="w-5 h-5" />
            <span className="uppercase tracking-widest text-xs font-black">Tekrar hoş geldin</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-[0.95] mb-6">
            Hesabına <br /> giriş yap.
          </h1>

          {inWebView && (
            <div
              className="mb-4 border-hard-2 bg-[#FFF3C2] rounded-xl p-3 text-sm leading-snug"
              data-testid="webview-warning"
              role="alert"
            >
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle strokeWidth={3} className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <div className="font-black">Uygulama içi tarayıcı algılandı</div>
                  <div className="text-[12px] text-[#333] mt-0.5">
                    Google güvenlik gereği bu tarayıcıda oturum açılamıyor. Linki <b>Safari/Chrome</b>&apos;da açman gerek.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={copyUrl}
                data-testid="copy-url-btn"
                className="w-full bg-white border-hard-2 rounded-lg px-3 py-2 text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 press-btn shadow-hard-sm"
              >
                {copied ? <Check strokeWidth={3} className="w-3.5 h-3.5" /> : <Copy strokeWidth={3} className="w-3.5 h-3.5" />}
                {copied ? "Kopyalandı — Safari/Chrome'a yapıştır" : "Linki kopyala"}
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={handleGoogle}
            disabled={inWebView}
            data-testid="google-login-btn"
            className={`w-full bg-white text-[#0A0A0A] font-bold border-hard shadow-hard rounded-xl px-5 py-3 flex items-center justify-center gap-3 press-btn ${inWebView ? "opacity-50 cursor-not-allowed" : ""}`}
            title={inWebView ? "Safari/Chrome'da aç" : undefined}
          >
            {GOOGLE_ICON}
            Google ile devam et
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-[2px] bg-[#0A0A0A]" />
            <span className="text-xs font-bold uppercase tracking-widest">veya</span>
            <div className="flex-1 h-[2px] bg-[#0A0A0A]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-black uppercase tracking-widest" htmlFor="email">
                E-posta
              </label>
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-[#F9F8F6] border-hard rounded-xl px-4 py-3 focus:outline-none focus:bg-white"
                placeholder="ornek@yappo.app"
              />
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-widest" htmlFor="password">
                Şifre
              </label>
              <input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-[#F9F8F6] border-hard rounded-xl px-4 py-3 focus:outline-none focus:bg-white"
                placeholder="En az 6 karakter"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm font-bold bg-[#FF9E80] border-hard-2 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full bg-[#CCFF00] text-[#0A0A0A] font-black border-hard shadow-hard rounded-xl px-5 py-3 press-btn disabled:opacity-60"
            >
              {submitting ? "Giriş yapılıyor…" : "Giriş Yap"}
            </button>
          </form>

          <p className="mt-6 text-sm">
            Hesabın yok mu?{" "}
            <Link data-testid="go-register-link" to="/register" className="font-black underline underline-offset-4 decoration-4 decoration-[#CCFF00]">
              Hemen kaydol
            </Link>
          </p>
          <div className="mt-8 flex justify-center">
            <MadeByBadge />
          </div>
        </div>
      </div>
    </div>
  );
}
